import {
  ASTKindToNode,
  ASTNode,
  DocumentNode,
  FragmentDefinitionNode,
  GraphQLSchema,
  OperationDefinitionNode,
  SelectionNode,
  TypeInfo,
  ValueNode,
  Visitor,
  getVisitFn,
  isAbstractType,
  parse,
  print,
  validate,
  visit,
  visitWithTypeInfo,
} from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useRef } from 'react';

import {
  getUsedVariablesString,
  parseVariablesOrDefault,
} from 'src/app/graph/explorerPage/helpers/jsonHelpers';
import {
  JSONObject,
  JSONValue,
} from 'src/app/graph/explorerPage/helpers/types';
import { useActiveDefinitionDetails } from 'src/app/graph/explorerPage/hooks/useActiveDefinitionDetails';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';

import { EditorHint } from '../editorHint/EditorHint';
import { visitThroughFragments } from '../hintsHelpers';

/**
 * Visit an ast while tracking the path to get to each field.
 */
function visitWithPath(
  path: string[],
  variables: JSONObject,
  typeInfo: TypeInfo,
  visitor: Visitor<ASTKindToNode>,
): Visitor<ASTKindToNode> {
  return {
    enter(node, ...rest) {
      // TODO (jason) this doesnt need to be by json path, could be by actual type path, ignoring aliasses
      switch (node.kind) {
        case 'Field': {
          const nodeName = node.name.value;
          path.push(
            `Field:${nodeName}(${JSON.stringify(
              node.arguments?.map((argument) =>
                variableToJSON(argument.value, variables),
              ) ?? [],
            )})`,
          );
          break;
        }
        case 'InlineFragment':
        case 'FragmentDefinition': {
          if (typeInfo.getType() !== typeInfo.getParentType()) {
            path.push(`InlineFragment:${node.typeCondition?.name.value ?? ''}`);
          }
          break;
        }
        default: {
          break;
        }
      }
      const fn = getVisitFn(visitor, node.kind, false);
      return fn?.apply(visitor, [node, ...rest]);
    },
    leave(node, ...rest) {
      const fn = getVisitFn(visitor, node.kind, true);
      const result = fn?.apply(visitor, [node, ...rest]);
      switch (node.kind) {
        case 'Field': {
          path.pop();
          break;
        }
        case 'InlineFragment':
        case 'FragmentSpread': {
          if (typeInfo.getType() !== typeInfo.getParentType()) {
            path.pop();
          }
          break;
        }
        default: {
          break;
        }
      }
      return result;
    },
  };
}

function isJSONObject(value: JSONValue | undefined): value is JSONObject {
  return Boolean(value && !Array.isArray(value) && typeof value === 'object');
}

/**
 * TODO name this better to imply map and filter
 */
function mapNestedArray(
  json: JSONValue | undefined,
  callback: (value: JSONValue | undefined) => JSONValue | undefined,
): JSONValue | undefined {
  if (Array.isArray(json)) {
    const result = json
      .map((value) => mapNestedArray(value, callback))
      .filter((value): value is JSONValue => value !== undefined);
    return result.length ? result : undefined;
  }
  return callback(json);
}

/**
 * Given some json, get only the values that implement a given type condition
 */
function filterJSONToSubtype(
  json: JSONValue | undefined,
  type: string,
  schema: GraphQLSchema,
): JSONValue | undefined {
  const schemaType = schema.getType(type);
  if (!schemaType) {
    return undefined;
  }
  const isMatchingType = (valueType: JSONValue | undefined) => {
    const valueSchemaType =
      typeof valueType === 'string' && schema.getType(valueType);
    return (
      valueSchemaType &&
      (schemaType === valueSchemaType ||
        (isAbstractType(schemaType) &&
          schema.isSubType(schemaType, valueSchemaType)))
    );
  };

  return mapNestedArray(json, (value) =>
    isJSONObject(value) && isMatchingType(value.__typename) ? value : undefined,
  );
}

/**
 * Visit an ast while tracking the JSON values at each field.
 */
function visitWithJSONValue(
  currentJSON: (JSONValue | undefined)[],
  schema: GraphQLSchema,
  visitor: Visitor<ASTKindToNode>,
): Visitor<ASTKindToNode> {
  return {
    enter(node, ...rest) {
      switch (node.kind) {
        case 'Field': {
          const nodeName = (node.alias ?? node.name).value;
          const json = currentJSON.slice(-1)[0];
          const newJson = mapNestedArray(json, (value) =>
            isJSONObject(value) && nodeName in value
              ? value[nodeName]
              : undefined,
          );
          currentJSON.push(newJson);
          break;
        }
        case 'InlineFragment':
        case 'FragmentDefinition': {
          const condition = node.typeCondition?.name.value;
          const json = currentJSON.slice(-1)[0];
          if (condition) {
            currentJSON.push(filterJSONToSubtype(json, condition, schema));
          } else {
            currentJSON.push(json);
          }
          break;
        }
        default: {
          break;
        }
      }
      const fn = getVisitFn(visitor, node.kind, false);
      return fn?.apply(visitor, [node, ...rest]);
    },
    leave(node, ...rest) {
      const fn = getVisitFn(visitor, node.kind, true);
      const result = fn?.apply(visitor, [node, ...rest]);
      switch (node.kind) {
        case 'Field':
        case 'InlineFragment':
        case 'FragmentDefinition': {
          currentJSON.pop();
          break;
        }
        default: {
          break;
        }
      }
      return result;
    },
  };
}

function addTypenameToDocument(operation: DocumentNode): DocumentNode {
  return visit(operation, {
    SelectionSet: {
      enter(node) {
        const hasTypename = node.selections.some(
          (selection) =>
            selection.kind === 'Field' && selection.name.value === '__typename',
        );
        if (!hasTypename) {
          return {
            ...node,
            selections: [
              ...node.selections,
              {
                kind: 'Field',
                name: {
                  kind: 'Name',
                  value: '__typename',
                },
              },
            ],
          };
        }
      },
    },
  });
}

function inlineFragments(document: DocumentNode) {
  const fragmentDefinitions: Record<string, FragmentDefinitionNode> = {};
  const newDocument = visitThroughFragments(document, {
    FragmentDefinition: {
      leave(node) {
        fragmentDefinitions[node.name.value] = node;
      },
    },
    SelectionSet: {
      leave(node) {
        const selections = node.selections;
        const newSelections: SelectionNode[] = [];
        selections.forEach((selection) => {
          if (selection.kind === 'FragmentSpread') {
            const fragmentDefinition =
              fragmentDefinitions[selection.name.value];
            newSelections.push({
              kind: 'InlineFragment',
              typeCondition: fragmentDefinition?.typeCondition,
              // TODO: remove non null assertion, added when enabling noUncheckedIndexedAccess
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              selectionSet: fragmentDefinition!.selectionSet,
            });
          } else {
            newSelections.push(selection);
          }
        });
        return {
          ...node,
          selections: newSelections,
        };
      },
    },
  });

  return newDocument?.definitions
    ? {
        ...newDocument,
        definitions: newDocument.definitions.filter(
          (definition) => definition.kind !== 'FragmentDefinition',
        ),
      }
    : newDocument;
}

function removeCachedFields(
  mergedResults: MergedResults,
  document: DocumentNode,
  variables: JSONObject,
  schema: GraphQLSchema,
): DocumentNode | null {
  const currentPath: string[] = [];
  const toDelete: SelectionNode[] = [];
  const usedArguments: Set<string> = new Set([]);
  const typeInfo = new TypeInfo(schema);

  // Inline fragments. We may want to request some of the fragments but not others.
  const inlinedDocument = inlineFragments(document);
  if (inlinedDocument === null) {
    return null;
  }

  const visitor: Visitor<ASTKindToNode, ASTNode> = {
    OperationDefinition: {
      leave(node) {
        if (node.selectionSet.selections.length === 0) {
          return null;
        }
        if (node.variableDefinitions) {
          const newVariables = node.variableDefinitions.filter((variable) =>
            usedArguments.has(variable.variable.name.value),
          );
          if (node.variableDefinitions.length !== newVariables.length) {
            return {
              ...node,
              variableDefinitions: newVariables.length
                ? newVariables
                : undefined,
            };
          }
        }
      },
    },
    SelectionSet: {
      leave(node) {
        const selections = node.selections.filter(
          (selection) => !toDelete.includes(selection),
        );
        return {
          ...node,
          selections,
        };
      },
    },
    InlineFragment: {
      leave(node) {
        if (node.selectionSet?.selections.length === 0) {
          toDelete.push(node);
        }
        return node;
      },
    },
    Field: {
      leave(node) {
        const pathString = currentPath.join('/');
        if (pathString in mergedResults) {
          toDelete.push(node);
        } else if (node.selectionSet?.selections.length === 0) {
          toDelete.push(node);
        } else {
          (node.arguments ?? []).forEach((argument) => {
            if (argument.value.kind === 'Variable') {
              usedArguments.add(argument.value.name.value);
            }
          });
        }
        return node;
      },
    },
  };

  return visit(
    inlinedDocument,
    visitWithTypeInfo(
      typeInfo,
      visitWithPath(currentPath, variables, typeInfo, visitor),
    ),
  );
}

function variableToJSON(value: ValueNode, variables: JSONObject): JSONValue {
  switch (value.kind) {
    case 'Variable':
      return variables[value.name.value] ?? null;
    case 'NullValue':
      return null;
    case 'ListValue': {
      return value.values.map((childValue) =>
        variableToJSON(childValue, variables),
      );
    }
    case 'ObjectValue': {
      const jsonValue: JSONObject = {};
      value.fields.forEach((field) => {
        jsonValue[field.name.value] = variableToJSON(field.value, variables);
      });
      return jsonValue;
    }
    default:
      return value.value;
  }
}

type MergedResults = Record<string, JSONValue>;

function addToResponseHints(
  mergedResults: MergedResults,
  document: DocumentNode,
  variables: JSONObject,
  response: JSONValue,
  schema: GraphQLSchema,
) {
  const hints: Record<number, string[]> = {};
  const operation = document.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  );

  if (!operation) {
    return [];
  }

  const currentPath: string[] = [];
  const currentJSON: JSONValue[] = [response];

  const visitor: Visitor<ASTKindToNode, ASTNode> = {
    Field: {
      enter(node) {
        const newJson = currentJSON.slice(-1)[0];
        const pathString = currentPath.join('/');
        const filteredJson = mapNestedArray(newJson, (value) =>
          value === null ||
          (typeof value !== 'object' && typeof value !== 'undefined')
            ? value
            : undefined,
        );
        if (filteredJson !== undefined) {
          // eslint-disable-next-line no-param-reassign
          mergedResults[pathString] = filteredJson;
        }
        const result = mergedResults[pathString];
        if (result !== undefined) {
          const hintText = JSON.stringify(result);
          if (node.loc) {
            const lineNumber = node.loc.startToken.line;
            getOrSetRecord(lineNumber, [], hints).push(hintText);
          }
        }
      },
    },
  };

  const typeInfo = new TypeInfo(schema);

  visitThroughFragments(
    document,
    visitWithTypeInfo(
      typeInfo,
      visitWithJSONValue(
        currentJSON,
        schema,
        visitWithPath(currentPath, variables, typeInfo, visitor),
      ),
    ),
  );

  return hints;
}

export function useResponseHints(
  activePartsOfDocument: string | undefined,
  variables: JSONObject,
  schema: GraphQLSchema,
  submitForResponseHints: (
    operation: string,
  ) => Promise<JSONObject | undefined>,
): Record<number, string[]> {
  const mergedResultsRef = useRef<MergedResults>({});
  const [hints, setHints] = React.useState<Record<number, string[]>>({});

  React.useEffect(() => {
    let canceled = false;
    (async () => {
      if (!activePartsOfDocument) {
        setHints([]);
        return;
      }
      const parsedDocument = parse(activePartsOfDocument);
      if (
        !parsedDocument ||
        parsedDocument.definitions.some(
          (definition) =>
            definition.kind === 'OperationDefinition' &&
            definition.operation !== 'query',
        )
      ) {
        setHints([]);
        return;
      }
      const prunedRequest = removeCachedFields(
        mergedResultsRef.current,
        parsedDocument,
        variables,
        schema,
      );
      let response: JSONObject | undefined;
      if (
        prunedRequest?.definitions?.length &&
        validate(schema, prunedRequest).length === 0
      ) {
        response = await submitForResponseHints(
          print(addTypenameToDocument(prunedRequest)),
        );
      }
      const newHints = addToResponseHints(
        mergedResultsRef.current,
        parsedDocument,
        variables,
        response ?? null,
        schema,
      );
      if (!canceled) {
        setHints(newHints);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [activePartsOfDocument, variables, schema, submitForResponseHints]);

  return hints;
}

/**
 * Attaches a result preview besides each reachable leaf field in the document
 */
export const ResponseHints = ({
  editor,
  variablesEditor,
  schema,
  submitForResponseHints,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  submitForResponseHints: (
    operation: string,
  ) => Promise<JSONObject | undefined>;
}) => {
  const activeDefinition = useActiveDefinitionDetails(editor, schema);
  const activePartsOfDocument = activeDefinition?.isValid
    ? activeDefinition.activePartsOfDocument
    : undefined;
  const documentLength = activePartsOfDocument?.split('\n')?.length ?? 0;

  const variables = useDerivedEditorValue({
    editor: variablesEditor,
    getValue: useCallback(
      () =>
        parseVariablesOrDefault(
          getUsedVariablesString(
            schema,
            editor,
            variablesEditor,
            editor.getPosition() ?? new monacoEditor.Position(1, 1),
          ),
        ),
      [schema, editor, variablesEditor],
    ),
    withPosition: false,
  });

  const hints = useResponseHints(
    activePartsOfDocument,
    variables,
    schema,
    submitForResponseHints,
  );

  return (
    <>
      {Object.entries(hints).map(([lineNumber, hint]) => (
        <EditorHint
          key={`${documentLength}-${lineNumber}-${hint}`}
          editor={editor}
          lineNumber={parseInt(lineNumber, 10)}
          hint={hint.join(', ')}
        />
      ))}
    </>
  );
};
